import { useEffect, useState } from "react";
import { collection, query, getDocs, orderBy, where, doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/init-firebase"
import { Center, FormControl } from "@chakra-ui/react";
import {
    Text,
    Wrap,
    WrapItem,
    Stack,
    Button,
    useColorModeValue,
    Flex,
    Box,
    Heading,
    Image,
    Avatar,
    Badge,
    HStack,
    VStack,
    Tag,
    TagLeftIcon,
    TagLabel,
    Tooltip,
    Link
} from '@chakra-ui/react';
import { useAuth } from "../../contexts/AuthContext";
import ReactDatePicker from "react-datepicker";
import { datepickerDark, datepickerLight } from "../../contexts/GlobalVariables";
import "react-datepicker/dist/react-datepicker.css";
import { Select } from "chakra-react-select";
import ReactLinkify from "react-linkify";
import { FiLock, FiClock, FiDollarSign, FiHash } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import moment from "moment";

export default function LockDeskReportingPage() {
    const history = useHistory();
    const collectionRef = collection(db, "RateLockRequests")
    const datepickerClass = useColorModeValue(datepickerLight, datepickerDark)
    const cardBg = useColorModeValue('white', 'gray.700')
    const { currentUser, SuperAdmin, users } = useAuth()

    const [lockRequests, setLockRequests] = useState(null)
    const [filteredLockRequests, setFilteredLockRequests] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState()

    // Set default date range to current week
    const getCurrentWeekDates = () => {
        const curr = new Date()
        const first = curr.getDate() - curr.getDay() + 1
        const firstDay = new Date(curr.setDate(first))
        firstDay.setHours(0)
        const lastDay = new Date()
        lastDay.setHours(23, 59, 59)
        return { firstDay, lastDay }
    }

    const { firstDay, lastDay } = getCurrentWeekDates()
    const [startDate, setStartDate] = useState(firstDay)
    const [endDate, setEndDate] = useState(lastDay)
    const [dateFilter, setDateFilter] = useState({label: 'Date Created', value: 'Date Created'})

    const [LO, setLO] = useState([])
    const [LC, setLC] = useState([])

    const baseQuery = query(collectionRef, 
        orderBy(dateFilter.value, 'desc'),
        where(dateFilter.value, '>=', startDate), 
        where(dateFilter.value, '<=', endDate),
        where('status', '==', 'Locked')
    )

    const reset = () => {
        setLoading(true)
        setLO([])
        setLC([])
        setLockRequests(null)
        setFilteredLockRequests(null)
        setStartDate(firstDay)
        setEndDate(lastDay)
        setError(null)
        setLoading(false)
    }

    const getLockRequests = async () => {
        try {
            setLoading(true)
            setError(null)
            setLockRequests(null)
            setFilteredLockRequests(null)
            
            let requests = []
            let totalTurnaroundTime = 0
            let lockedCount = 0

            // If nobody is selected, fetch all lock requests for this date range
            if(LO.length === 0 && LC.length === 0) {
                const querySnapshot = await getDocs(baseQuery)
                querySnapshot.forEach((doc) => {
                    const request = doc.data()
                    if (request.status === 'Locked') {
                        requests.push(request)
                        // Calculate turnaround time if we have both created and locked timestamps
                        if (request['Date Created'] && request['Date Locked']) {
                            const createdTime = request['Date Created'].toDate()
                            const lockedTime = request['Date Locked'].toDate()
                            const turnaroundTime = (lockedTime - createdTime) / (1000 * 60 * 60) // Convert to hours
                            totalTurnaroundTime += turnaroundTime
                            lockedCount++
                        }
                    }
                })
            } else {
                // Handle LO filter
                for(const loanOfficer of LO) {
                    const thisLOQuery = query(baseQuery, where('requestedBy.uid', '==', loanOfficer.value))
                    const querySnapshot = await getDocs(thisLOQuery)
                    querySnapshot.forEach((doc) => {
                        const request = doc.data()
                        if (request.status === 'Locked') {
                            requests.push(request)
                            if (request['Date Created'] && request['Date Locked']) {
                                const createdTime = request['Date Created'].toDate()
                                const lockedTime = request['Date Locked'].toDate()
                                const turnaroundTime = (lockedTime - createdTime) / (1000 * 60 * 60)
                                totalTurnaroundTime += turnaroundTime
                                lockedCount++
                            }
                        }
                    })
                }

                // Handle LC filter
                for(const lenderConcierge of LC) {
                    const thisLCQuery = query(baseQuery, where('lockedBy.uid', '==', lenderConcierge.value))
                    const querySnapshot = await getDocs(thisLCQuery)
                    querySnapshot.forEach((doc) => {
                        const request = doc.data()
                        if (request.status === 'Locked') {
                            requests.push(request)
                            if (request['Date Created'] && request['Date Locked']) {
                                const createdTime = request['Date Created'].toDate()
                                const lockedTime = request['Date Locked'].toDate()
                                const turnaroundTime = (lockedTime - createdTime) / (1000 * 60 * 60)
                                totalTurnaroundTime += turnaroundTime
                                lockedCount++
                            }
                        }
                    })
                }
            }

            const averageTurnaroundTime = lockedCount > 0 ? totalTurnaroundTime / lockedCount : 0

            setLockRequests(requests)
            setFilteredLockRequests(requests)
            
            setLoading(false)
        } catch(err) {
            console.error(err)
            setLoading(false)
            setError(err.message)
        }
    }

    const getClientStatus = async (loanID) => {
        try {
            const clientDoc = await getDoc(doc(db, "Clients", loanID));
            if (clientDoc.exists()) {
                return clientDoc.data().status || "completed";
            }
            return "completed";
        } catch (err) {
            console.error("Error getting client status:", err);
            return "completed";
        }
    };

    const handleViewClient = async (loanID) => {
        const status = await getClientStatus(loanID);
        history.push(`/${status.toLowerCase()}/${loanID}`);
    };

    useEffect(() => {
        getLockRequests()
    }, [])

    if (!SuperAdmin) {
        return (
            <Box p={4}>
                <Text>You do not have permission to access this page.</Text>
            </Box>
        )
    }

    return (
        <Flex flexDir={{base: 'column-reverse', xl: 'row'}} mt='5'>
            <Box w={{base: '90vw', md: '75vw', xl: '65vw'}}>
                {!loading ? (
                    <>
                        {filteredLockRequests && !error && (
                            <Box p={4} bg={cardBg} borderRadius="lg" shadow="sm">
                                <Flex justify="space-between" align="center">
                                    <HStack>
                                        <Text fontWeight="bold">Average Turnaround Time:</Text>
                                        <Text>
                                            {filteredLockRequests.length > 0 ? 
                                                (() => {
                                                    const avgHours = filteredLockRequests.reduce((acc, req) => {
                                                        if (req['Date Created'] && req['Date Locked']) {
                                                            const createdTime = req['Date Created'].toDate()
                                                            const lockedTime = req['Date Locked'].toDate()
                                                            return acc + ((lockedTime - createdTime) / (1000 * 60 * 60))
                                                        }
                                                        return acc
                                                    }, 0) / filteredLockRequests.length;
                                                    
                                                    const hours = Math.floor(avgHours);
                                                    const minutes = Math.round((avgHours - hours) * 60);
                                                    
                                                    return `${hours}h ${minutes}m`;
                                                })()
                                                : 'No data available'}
                                        </Text>
                                    </HStack>
                                </Flex>
                            </Box>
                        )}
                        <Stack mt={3} spacing={3}>
                            {filteredLockRequests && filteredLockRequests.map((request, index) => (
                                <Box 
                                    key={`request-${index}`}
                                    p={4} 
                                    bg={cardBg} 
                                    borderRadius="lg" 
                                    shadow="sm"
                                    _hover={{ shadow: "md" }}
                                >
                                    <Flex justify="space-between" align="center" mb={3}>
                                        <HStack>
                                            <Avatar size="sm" src={request.requestedBy?.photoURL} />
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="medium">{request.requestedBy?.name}</Text>
                                                <Text fontSize="sm" color="gray.500">
                                                    {request['Date Created']?.toDate() ? 
                                                        moment(request['Date Created'].toDate()).calendar() : null}
                                                </Text>
                                            </VStack>
                                        </HStack>
                                        <Button 
                                            size="sm" 
                                            colorScheme="blue"
                                            onClick={() => handleViewClient(request.loanID)}
                                        >
                                            View File
                                        </Button>
                                    </Flex>

                                    <Flex justify="space-between" align="center">
                                        <HStack spacing={2}>
                                            <Tag size="md" variant="outline" colorScheme="green">
                                                <TagLeftIcon as={FiLock} />
                                                <TagLabel>{request.rate}%</TagLabel>
                                            </Tag>
                                            <Tag size="md" variant="outline">
                                                <TagLeftIcon as={FiClock} />
                                                <TagLabel>{request.termDays} days</TagLabel>
                                            </Tag>
                                            <Tag size="md" variant="outline">
                                                <TagLeftIcon as={FiDollarSign} />
                                                <TagLabel>
                                                    {request.pricingType || 'LP'}: {request.points} points (${request.cost})
                                                </TagLabel>
                                            </Tag>
                                            {request.loanNumber && (
                                                <Tag size="md" variant="outline">
                                                    <TagLeftIcon as={FiHash} />
                                                    <TagLabel>{request.loanNumber}</TagLabel>
                                                </Tag>
                                            )}
                                        </HStack>
                                        <HStack spacing={2}>
                                            <HStack spacing={1}>
                                                <Text fontSize="sm" color="gray.500">Locked by:</Text>
                                                <Avatar size="xs" src={request.lockedBy?.photoURL} />
                                                <Text fontSize="sm">{request.lockedBy?.name}</Text>
                                            </HStack>
                                            <Text fontSize="sm" color="gray.500">
                                                {request['Date Locked']?.toDate() ? 
                                                    moment(request['Date Locked'].toDate()).calendar() : null}
                                            </Text>
                                        </HStack>
                                    </Flex>
                                </Box>
                            ))}
                            {!filteredLockRequests && !error && <Text>Start by building a query using the form on the right</Text>}
                            {filteredLockRequests && filteredLockRequests.length === 0 && !error && <Text>No lock requests found</Text>}
                        </Stack>
                    </>
                ) : (
                    <Center mt='10'>
                        <Image src={require('../../assets/img/the-future-is-loading-transparent.gif')}/>
                    </Center>
                )}
                {error && <ReactLinkify><Text>{error}</Text></ReactLinkify>}
            </Box>

            <Box w={{base: '90vw', md:'75vw', xl: '20vw'}} mt={3}>
                <Stack mt={1} direction='column' maxW={'1200px'} spacing='4'>
                    <Heading textAlign={'center'} size='md' as='em'>Build-A-Query</Heading>
                    <Stack spacing={1}>
                        <Stack direction={{base: 'row', xl: 'column'}}>
                            <ReactDatePicker   
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                className={datepickerClass}
                                placeholderText='Start Date'
                            />
                            <ReactDatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                className={datepickerClass}
                                placeholderText='End Date'
                            />
                        </Stack>
                    </Stack>

                    <FormControl mb={3}>
                        <Text p={1} fontSize={'xs'}>Loan Officer (Requested By)</Text>
                        <Select             
                            options={users}
                            value={LO}
                            onChange={(e) => setLO(e)}
                            placeholder="Everyone"
                            selectedOptionColor="green"
                            classNamePrefix={'lo-filter-select'}
                            closeMenuOnSelect='false'
                            isClearable
                            isMulti
                        />
                    </FormControl>

                    <FormControl mb={3}>
                        <Text p={1} fontSize={'xs'}>Lender Concierge (Locked By)</Text>
                        <Select
                            options={users}
                            value={LC}
                            onChange={(e) => setLC(e)}
                            placeholder="Everyone"
                            selectedOptionColor="green"
                            classNamePrefix={'lc-filter-select'}
                            isClearable
                            isMulti
                        />
                    </FormControl>

                    <Flex w='100%' flexDir={{base: 'row', xl: 'column'}}>
                        <Button w={{base: '50%', xl: '100%'}} p={5} pl={7} pr={7} m={1} onClick={getLockRequests} size='md' colorScheme='green'>Apply</Button>
                        <Button w={{base: '50%', xl: '100%'}} p={5} pl={7} pr={7} m={1} onClick={reset} size='md' colorScheme='blue'>Reset</Button>
                    </Flex>
                </Stack>
            </Box>
        </Flex>
    )
} 